import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {useForm} from 'react-hook-form'
import {Box, Button, Flex, SimpleGrid, useDisclosure} from '@chakra-ui/react'
import useAsync from '../../../commerce-api/hooks/useAsync'
import useCustomer from '../../../commerce-api/hooks/useCustomer'
import {MarkerPin01Icon} from '../../../components/icons'
import LoadingSpinner from '../../../components/loading-spinner'
import {API_ERROR_MESSAGE} from '../../../constants'
import {useCountry} from '../../../hooks'
import useEffectEvent from '../../../hooks/use-effect-event'
import {useToast} from '../../../hooks/use-toast'
import MyStokkeLayoutDefault from '../layouts/default'
import MyStokkeAddressCard from '../partials/address-card'
import MyStokkeAddressForm from '../partials/address-form'
import MyStokkeBackToStokke from '../partials/back-to-stokke'
import MyStokkeFooter from '../partials/footer'
import MyStokkeLayoutEmpty from '../layouts/empty'
import ConfirmationModal from '../../../components/confirmation-modal'

const AddressArrowIcon = (props) => (
    <Box
        width={2}
        height={2}
        borderLeft="2px"
        borderTop="2px"
        borderColor="stokkeCore.black"
        position="absolute"
        left="50%"
        bottom="-22px"
        backgroundColor="stokkeGray.light"
        transform="rotate(45deg)"
        zIndex={1}
        {...props}
    />
)

const MyStokkeAddresses = () => {
    const {formatMessage} = useIntl()
    const country = useCountry()
    const customer = useCustomer()
    const addresses = useMemo(
        () =>
            (customer.addresses || [])
                .filter((address) => address.countryCode === country)
                .map((address, idx) => ({
                    ...address,
                    preferred: idx === 0
                })),
        [customer.addresses, country]
    )
    const toast = useToast()
    const hasAddresses = addresses.length > 0
    const form = useForm()
    const {reset: formReset, trigger: formTrigger} = form
    const [selectedAddressId, setSelectedAddressId] = useState()
    const [isEditingAddress, setIsEditingAddress] = useState(false)
    const [addressToRemove, setAddressToRemove] = useState()
    const confirmationModalProps = useDisclosure({
        isOpen: !!addressToRemove,
        onClose: () => setAddressToRemove()
    })
    const {
        execute: onSubmit,
        loading: isSaving,
        error: saveError,
        data: saveResult
    } = useAsync(async (address) => {
        if (selectedAddressId) {
            address = {...address, addressId: selectedAddressId}
        }

        await customer.addSavedAddress(address)

        return true
    })
    const {
        execute: removeAddress,
        loading: isRemoving,
        error: removeError
    } = useAsync(async (address) => customer.removeSavedAddress(address.addressId))

    const onError = useEffectEvent(() => {
        toast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    })
    useEffect(() => {
        if (saveError) {
            console.error('Error saving address', saveError)
            onError()
        }
    }, [saveError, onError])
    useEffect(() => {
        if (removeError) {
            console.error('Error removing address', removeError)
            onError()
        }
    }, [removeError, onError])
    useEffect(() => {
        if (saveResult) {
            formReset({addressId: ''})
            setIsEditingAddress(false)
        }
    }, [saveResult, formReset])

    const onAddNewAddress = useCallback(() => {
        setSelectedAddressId(undefined)
        formReset({addressId: ''})
        setIsEditingAddress((state) => !state)
        formTrigger()
    }, [formReset, formTrigger])
    const onEdit = useCallback(
        (address) => {
            setSelectedAddressId(address.addressId)
            formReset({...address})
            setIsEditingAddress(true)
            formTrigger()
        },
        [formReset, formTrigger]
    )
    const onCancel = useCallback(() => setIsEditingAddress(false), [])
    const onRemove = useCallback((address) => {
        setAddressToRemove(address)
    }, [])

    if (!hasAddresses && !isEditingAddress) {
        return (
            <MyStokkeLayoutEmpty
                icon={<MarkerPin01Icon />}
                mainLabel={formatMessage({
                    defaultMessage: 'No saved addresses yet.',
                    id: 'mystokke.addresses.empty.headline'
                })}
                secondaryLabel={formatMessage({
                    defaultMessage:
                        'When you save an address, you’ll see it here. Click on the button to start adding.',
                    id: 'mystokke.addresses.empty.text'
                })}
            >
                <MyStokkeFooter>
                    <Button variant="solid" onClick={onAddNewAddress}>
                        <FormattedMessage
                            defaultMessage="Add a new address"
                            id="mystokke.addresses.button.add-address"
                        />
                    </Button>
                    <MyStokkeBackToStokke />
                </MyStokkeFooter>
            </MyStokkeLayoutEmpty>
        )
    }

    return (
        <MyStokkeLayoutDefault loading={isRemoving}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <Flex flexDirection="column" gap={4}>
                    <Flex flexDirection="column" gap={4}>
                        <SimpleGrid
                            columns={{base: 1, md: 2}}
                            spacing={4}
                            gridAutoFlow="row dense"
                            role="list"
                            aria-label={formatMessage({
                                defaultMessage: 'Address List',
                                id: 'mystokke.addresses.list.label'
                            })}
                        >
                            {addresses.map((address, idx) => (
                                <React.Fragment key={address.addressId}>
                                    <MyStokkeAddressCard
                                        address={address}
                                        idx={idx}
                                        onEdit={onEdit}
                                        onRemove={!address.preferred ? onRemove : null}
                                    >
                                        {/*Arrow up icon pointing to the address that is being edited*/}
                                        {isEditingAddress &&
                                            address.addressId === selectedAddressId && (
                                                <AddressArrowIcon bottom="-22px" />
                                            )}
                                    </MyStokkeAddressCard>
                                    {isEditingAddress &&
                                        address.addressId === selectedAddressId && (
                                            <Box
                                                border="2px"
                                                gridColumn={{base: 1, md: 'span 2'}}
                                                position="relative"
                                            >
                                                {isSaving && <LoadingSpinner />}
                                                <MyStokkeAddressForm
                                                    form={form}
                                                    onCancel={onCancel}
                                                />
                                            </Box>
                                        )}
                                </React.Fragment>
                            ))}

                            {(hasAddresses || !isEditingAddress) && (
                                <Box
                                    role="listitem"
                                    display={
                                        isEditingAddress && !selectedAddressId
                                            ? null
                                            : {base: 'none', md: 'flex'}
                                    }
                                    maxWidth={{base: '25rem', md: 'none'}}
                                    marginX={{base: 'auto', md: 0}}
                                    width={{base: '100%', md: 'auto'}}
                                >
                                    <Button
                                        variant="blackCard"
                                        minHeight={{base: '56px', md: '156px'}}
                                        height="100%"
                                        width="100%"
                                        size="lg"
                                        onClick={onAddNewAddress}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Add a new address"
                                            id="mystokke.addresses.button.add-address"
                                        />
                                        {/*Arrow up icon pointing to the new address that is being added*/}
                                        {isEditingAddress && !selectedAddressId && (
                                            <AddressArrowIcon bottom="-22px" />
                                        )}
                                    </Button>
                                </Box>
                            )}
                        </SimpleGrid>

                        {isEditingAddress && !selectedAddressId && (
                            <Box border={hasAddresses ? '2px' : 'none'} position="relative">
                                {isSaving && <LoadingSpinner />}
                                <MyStokkeAddressForm form={form} onCancel={onCancel} />
                            </Box>
                        )}
                    </Flex>

                    <MyStokkeFooter>
                        <Button
                            display={
                                isEditingAddress ? 'none' : hasAddresses ? {md: 'none'} : 'flex'
                            }
                            variant="solid"
                            onClick={onAddNewAddress}
                        >
                            <FormattedMessage
                                defaultMessage="Add a new address"
                                id="mystokke.addresses.button.add-address"
                            />
                        </Button>
                        <MyStokkeBackToStokke />
                    </MyStokkeFooter>
                </Flex>
                <ConfirmationModal
                    {...confirmationModalProps}
                    onPrimaryAction={() => {
                        setAddressToRemove()
                        removeAddress(addressToRemove)
                    }}
                    onAlternateAction={() => {
                        setAddressToRemove()
                    }}
                />
            </form>
        </MyStokkeLayoutDefault>
    )
}

export default MyStokkeAddresses
